import React, { useState } from "react";
import "../styles/LoginPage.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import useContactForm from "../hooks/useContactForm";
import ContactContent from "./content/ContactContent";
import Agent101Content from "./content/Agent101Content";
import Prompting101Content from "./content/Prompting101Content";
import TermsOfServiceContent from "./content/TermsOfServiceContent";
import { GoogleIcon, GitHubIcon, MicrosoftIcon, EmailIcon } from "./subcomponents/LoginIcons";

export default function LoginPage({ redirectTo = '/chat' }) {
  const navigate = useNavigate();
  const { startOAuth, startEmailLogin, verifyEmailCode } = useAuth();
  
  const API_URL = process.env.REACT_APP_AUTH_BACKEND_URL;
  const { contact, setContact, handleContactSubmit, contactStatus } = useContactForm(API_URL);

  const [emailMode, setEmailMode] = useState(false);
  const [verifyMode, setVerifyMode] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showContact, setShowContact] = useState(false);
  const [showAgent101, setShowAgent101] = useState(false);
  const [showPrompting101, setShowPrompting101] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const buttonStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 24px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    fontWeight: 500,
    fontSize: "14px",
    fontFamily: "Roboto, sans-serif",
    cursor: "pointer",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    transition: "background-color .2s, box-shadow .2s",
    width: "220px",
    marginBottom: "10px",
  };

  const googleButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#fff",
    color: "#444",
  };

  const githubButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#f5f5f5",
    color: "#333",
  };

  const microsoftButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#fff",
    color: "#444",
  };

  const emailButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#fff",
    color: "#444",
  };

  const inputStyle = {
    padding: "10px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    width: "220px",
    marginBottom: "10px",
    fontSize: "14px",
    boxSizing: "border-box"
  };

  const submitButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#4285F4",
    color: "#fff",
    border: "none",
  };

  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 3000,
  };

  const popupContentStyle = {
    backgroundColor: "#23252e",
    color: "#ffffff",
    padding: "24px",
    borderRadius: "8px",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "80vh",
    overflowY: "auto",
    position: "relative",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
    border: "1px solid #343842",
    textAlign: "left",
    "--text-color": "#ffffff",
    "--strong-text-color": "#ffffff",
    "--heading-color": "#ffffff",
    "--panel-background": "#23252e",
    "--panel-border": "#343842",
    "--input-background": "#1a1c23",
    "--input-border": "#343842",
    "--input-text": "#ffffff",
    "--link-color": "#007bff"
  };

  const closeButtonStyle = {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#b5b9c9",
    padding: "0",
    lineHeight: "1",
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const result = await startEmailLogin(email);
    setLoading(false);
    
    if (result.success) {
      setVerifyMode(true);
    } else {
      setError(result.error || "Failed to send login code");
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    const result = await verifyEmailCode(email, code);
    setLoading(false);
    
    if (result.error) {
      setError(result.error);
    } else if (result.isNew) {
      navigate("/register", { replace: true, state: { redirectTo } });
    } else {
      navigate(redirectTo || "/chat", { replace: true });
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <div className="login-welcome" style={{ marginBottom: '0.5rem' }}>
          stackform.<span className="gradient-beta">beta</span>
        </div>
        <picture>
          <source srcSet="/stackformAI.webp" type="image/webp" />
          <img src="/stackformAI-sm.png" alt="Logo" className="login-logo" />
        </picture>
        
        {!emailMode ? (
          <>
            <div className="login-prompt" style={{ textAlign: 'center', marginBottom: '0.1rem' }}>Sign in/Register with:</div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button
                style={githubButtonStyle}
                onClick={() => { if (redirectTo && redirectTo !== '/chat') localStorage.setItem('sf_auth_redirect', redirectTo); startOAuth("github"); }}
              >
                <GitHubIcon />
                GitHub
              </button>

              <button
                style={googleButtonStyle}
                onClick={() => { if (redirectTo && redirectTo !== '/chat') localStorage.setItem('sf_auth_redirect', redirectTo); startOAuth("google"); }}
              >
                <GoogleIcon />
                Google
              </button>

              <button
                style={microsoftButtonStyle}
                onClick={() => { if (redirectTo && redirectTo !== '/chat') localStorage.setItem('sf_auth_redirect', redirectTo); startOAuth("microsoft"); }}
              >
                <MicrosoftIcon />
                Microsoft
              </button>

              <div style={{ display: 'flex', alignItems: 'center', width: '220px', margin: '10px 0' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#343842' }}></div>
                <span style={{ padding: '0 10px', color: '#b5b9c9', fontSize: '12px' }}>OR</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#343842' }}></div>
              </div>

              <button
                style={emailButtonStyle}
                onClick={() => setEmailMode(true)}
              >
                <EmailIcon />
                Email
              </button>
            </div>
          </>
        ) : !verifyMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="login-prompt" style={{ textAlign: 'center', marginBottom: '1rem' }}>Enter your email:</div>
            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <input
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
                required
              />
              <button
                type="submit"
                style={submitButtonStyle}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Login Code"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmailMode(false);
                  setError(null);
                }}
                style={{ ...buttonStyle, marginTop: "10px", width: "auto", border: "none", boxShadow: "none", background: "none", color: "#666" }}
              >
                Back
              </button>
            </form>
            {error && <div style={{ color: "red", marginTop: "10px", fontSize: "14px" }}>{error}</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="login-prompt" style={{ textAlign: 'center', marginBottom: '1rem' }}>Enter verification code:</div>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
              Sent to {email}<br/>
              (Code expires in 15 minutes)
            </div>
            <form onSubmit={handleVerifySubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={inputStyle}
                required
              />
              <button
                type="submit"
                style={submitButtonStyle}
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify & Login"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setVerifyMode(false);
                  setError(null);
                }}
                style={{ ...buttonStyle, marginTop: "10px", width: "auto", border: "none", boxShadow: "none", background: "none", color: "#666" }}
              >
                Back
              </button>
            </form>
            {error && <div style={{ color: "red", marginTop: "10px", fontSize: "14px" }}>{error}</div>}
          </div>
        )}

      </div>

      {/* Links at the bottom */}
      <div style={{ marginTop: '10px', display: 'flex', gap: '15px', fontSize: '13px', color: 'var(--link-color, #636cff)', flexWrap: 'wrap', justifyContent: 'center' }}>
        <span style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 'bold' }} onClick={() => setShowContact(true)}>Contact Us</span>
        <span style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 'bold' }} onClick={() => setShowAgent101(true)}>Agent</span>
        <span style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 'bold' }} onClick={() => setShowPrompting101(true)}>Prompting</span>
        <span style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 'bold' }} onClick={() => setShowTerms(true)}>Terms of Service</span>
      </div>

      {/* Microsoft for Startups Badge (local, no external request) */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <span
          aria-label="Microsoft for Startups"
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '4px',
            backgroundColor: '#0078d4',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            cursor: 'default',
          }}
        >
          Microsoft for Startups
        </span>
      </div>

      {/* Popups */}
      {showContact && (
        <div style={popupOverlayStyle} onClick={() => setShowContact(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <button style={closeButtonStyle} onClick={() => setShowContact(false)}>&times;</button>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#fff' }}>Contact Us</h2>
            <ContactContent contact={contact} setContact={setContact} handleContactSubmit={handleContactSubmit} contactStatus={contactStatus} />
          </div>
        </div>
      )}

      {showAgent101 && (
        <div style={popupOverlayStyle} onClick={() => setShowAgent101(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <button style={closeButtonStyle} onClick={() => setShowAgent101(false)}>&times;</button>
            <Agent101Content forceDarkMode={true} />
          </div>
        </div>
      )}

      {showPrompting101 && (
        <div style={popupOverlayStyle} onClick={() => setShowPrompting101(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <button style={closeButtonStyle} onClick={() => setShowPrompting101(false)}>&times;</button>
            <Prompting101Content />
          </div>
        </div>
      )}

      {showTerms && (
        <div style={popupOverlayStyle} onClick={() => setShowTerms(false)}>
          <div style={popupContentStyle} onClick={(e) => e.stopPropagation()}>
            <button style={closeButtonStyle} onClick={() => setShowTerms(false)}>&times;</button>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#fff' }}>Terms of Service</h2>
            <TermsOfServiceContent onNavigateToContact={() => { setShowTerms(false); setShowContact(true); }} />
          </div>
        </div>
      )}
    </div>
  );
}